import type { ApiError } from '../api/client';

/**
 * Browser download helpers. Both work by clicking a temporary `<a>` so the
 * current page never navigates away.
 */

/** Same-origin attachment download by URL — the endpoint sends
 * `Content-Disposition: attachment`, cookie auth rides along. */
export function triggerDownload(url: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.rel = 'noreferrer';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/** Saves bytes already fetched (e.g. via axios `responseType: 'blob'`) under
 * `filename`. Used when the caller needs a loading state or error handling
 * around a long-running download, which a plain URL click cannot offer. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    anchor.rel = 'noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    // Revoke on the next tick: some browsers start the download asynchronously.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
}

const FILENAME_RE = /filename="([^"]+)"/;

/** The `filename="…"` from a `Content-Disposition` header, if any. */
export function filenameFromDisposition(disposition: string | undefined): string | null {
  const match = disposition ? FILENAME_RE.exec(disposition) : null;
  return match?.[1] ?? null;
}

/**
 * With `responseType: 'blob'` axios hands back the ERROR body as a Blob too,
 * so the interceptor rejects with a Blob instead of the JSON envelope. Decode
 * it here so callers keep using `getApiError` as everywhere else.
 */
export async function rethrowBlobAsApiError(error: unknown): Promise<never> {
  if (error instanceof Blob) {
    try {
      throw JSON.parse(await error.text()) as ApiError;
    } catch (parsed) {
      if (parsed && typeof parsed === 'object' && 'code' in parsed) throw parsed;
    }
  }
  throw error;
}
